import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { webCrypto } from '@/lib/crypto/web-crypto'
import { UserRole, Permission, RBAC } from '@/lib/auth/rbac'
import { MFA } from '@/lib/auth/mfa'
import { CSRF } from '@/lib/security/csrf'
import { constantTimeEqual } from '@/lib/crypto/constant-time'

interface AuthState {
  isAuthenticated: boolean
  encryptionKey: CryptoKey | null
  sessionId: string | null
  lastActivity: Date | null
  autoLockTimeout: number // minutes
  
  // User & RBAC
  user: {
    id: string
    email: string
    role: UserRole
    mfaEnabled: boolean
    mfaSecret?: string
  } | null
  
  // MFA state
  mfaPending: boolean
  mfaBackupCodes: string[] | null
  
  // Actions
  authenticate: (password: string, mfaCode?: string) => Promise<boolean>
  setupMFA: () => Promise<{ qrCode: string; backupCodes: string[] }>
  verifyMFA: (code: string) => Promise<boolean>
  logout: () => void
  updateActivity: () => void
  setAutoLockTimeout: (minutes: number) => void
  checkAutoLock: () => boolean
  hasPermission: (permission: Permission) => boolean
}

const CORRECT_PASSWORD = 'QQQ' // This should be configurable in production
const SESSION_TIMEOUT = 30 // minutes

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      isAuthenticated: true, // Authentication disabled - always authenticated
      encryptionKey: null,
      sessionId: null,
      lastActivity: null,
      autoLockTimeout: SESSION_TIMEOUT,
      user: null,
      mfaPending: false,
      mfaBackupCodes: null,

      authenticate: async (password: string, mfaCode?: string): Promise<boolean> => {
        try {
          // Authentication disabled - always return true
          // Keeping the structure for potential future re-enablement
          
          // Skip password validation - authentication is disabled
          // const encoder = new TextEncoder()
          // const passwordBytes = encoder.encode(password)
          // const correctBytes = encoder.encode(CORRECT_PASSWORD)
          
          // const maxLen = Math.max(passwordBytes.length, correctBytes.length)
          // const paddedPassword = new Uint8Array(maxLen)
          // const paddedCorrect = new Uint8Array(maxLen)
          // paddedPassword.set(passwordBytes)
          // paddedCorrect.set(correctBytes)
          
          // if (!constantTimeEqual(paddedPassword, paddedCorrect)) {
          //   return false
          // }

          // Temporary user until proper user management is implemented
          const user = {
            id: 'default-user',
            email: 'user@qkey.co',
            role: UserRole.USER,
            mfaEnabled: false, // Will be true once MFA is set up
            mfaSecret: undefined
          }

          // Check if MFA is required
          if (user.mfaEnabled && user.mfaSecret) {
            if (!mfaCode) {
              set({ mfaPending: true })
              return false
            }
            
            const isValidMFA = MFA.verifyTOTP(user.mfaSecret, mfaCode)
            if (!isValidMFA) {
              return false
            }
          }

          // Generate session encryption key from password
          const salt = webCrypto.generateRandomBytes(32)
          const keyMaterial = await webCrypto.pbkdf2(password, salt, 100000, 32)
          
          const encryptionKey = await crypto.subtle.importKey(
            'raw',
            keyMaterial as BufferSource,
            { name: 'AES-GCM' },
            false,
            ['encrypt', 'decrypt']
          )

          const sessionId = webCrypto.bytesToHex(webCrypto.generateRandomBytes(16))
          const now = new Date()

          set({
            isAuthenticated: true,
            encryptionKey,
            sessionId,
            lastActivity: now,
            user,
            mfaPending: false
          })

          // Initialize CSRF token for the session
          CSRF.initializeToken()

          // Clear sensitive data
          webCrypto.clearMemory(keyMaterial)
          
          return true
        } catch (error) {
          console.error('Authentication failed:', error)
          return false
        }
      },
      
      setupMFA: async () => {
        const { user } = get()
        if (!user) throw new Error('User not authenticated')
        
        const mfaSetup = await MFA.setupMFA(user.id, user.email)
        
        // Store the secret (in production, this should be encrypted)
        set({
          user: {
            ...user,
            mfaEnabled: true,
            mfaSecret: mfaSetup.secret
          },
          mfaBackupCodes: mfaSetup.backupCodes
        })
        
        return {
          qrCode: mfaSetup.qrCode,
          backupCodes: mfaSetup.backupCodes
        }
      },
      
      verifyMFA: async (code: string): Promise<boolean> => {
        const { user, mfaPending } = get()
        
        if (!user || !user.mfaSecret || !mfaPending) {
          return false
        }
        
        const isValid = MFA.verifyTOTP(user.mfaSecret, code)
        
        if (isValid) {
          set({ 
            isAuthenticated: true,
            mfaPending: false 
          })
        }
        
        return isValid
      },

      logout: () => {
        // Clear CSRF token on logout
        CSRF.clearToken()
        
        set({
          isAuthenticated: false,
          encryptionKey: null,
          sessionId: null,
          lastActivity: null,
          user: null,
          mfaPending: false,
          mfaBackupCodes: null
        })
      },

      updateActivity: () => {
        set({ lastActivity: new Date() })
      },

      setAutoLockTimeout: (minutes: number) => {
        set({ autoLockTimeout: minutes })
      },

      checkAutoLock: (): boolean => {
        const { lastActivity, autoLockTimeout, isAuthenticated } = get()
        
        if (!isAuthenticated || !lastActivity) {
          return false
        }

        const now = new Date()
        const timeDiff = (now.getTime() - lastActivity.getTime()) / 1000 / 60 // minutes
        
        if (timeDiff > autoLockTimeout) {
          get().logout()
          return true
        }
        
        return false
      },
      
      hasPermission: (permission: Permission): boolean => {
        const { user } = get()
        if (!user) return false
        return RBAC.hasPermission(user.role, permission)
      },
    }),
    {
      name: 'qkey-auth',
      // Only persist non-sensitive data
      partialize: (state) => ({
        autoLockTimeout: state.autoLockTimeout,
        // Don't persist authentication state - require login on each session
        // isAuthenticated: state.isAuthenticated,
        // sessionId: state.sessionId,
        // user: state.user ? {
        //   id: state.user.id,
        //   email: state.user.email,
        //   role: state.user.role,
        //   mfaEnabled: state.user.mfaEnabled
        // } : null,
      }),
    }
  )
)

// Auto-lock check on activity
let autoLockInterval: number | null = null

export const startAutoLockMonitor = () => {
  if (autoLockInterval) {
    clearInterval(autoLockInterval)
  }

  autoLockInterval = window.setInterval(() => {
    useAuthStore.getState().checkAutoLock()
  }, 60000) // Check every minute
}

export const stopAutoLockMonitor = () => {
  if (autoLockInterval) {
    clearInterval(autoLockInterval)
    autoLockInterval = null
  }
}